<template>
  <div class="space-y-8 animate-fade-in">

    <!-- HEADER -->
    <div class="flex flex-col md:flex-row md:items-end justify-between gap-4">
      <div>
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400 capitalize">
          {{ formattedDate }}
        </p>
        <h1 class="text-3xl font-bold text-slate-900 dark:text-white tracking-tight mt-1">
          {{ greeting }}, <span class="text-[#F36421]">Central LLEVO</span>
        </h1>
      </div>
      <div class="flex gap-3">
        <button class="relative flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-200 hover:shadow-md transition-all">
          <Bell class="w-4 h-4 text-slate-400" />
          <span class="w-2 h-2 bg-red-500 rounded-full absolute ml-3 mb-3 animate-pulse"></span>
          Alertas
        </button>
        <button @click="openCreateOrder" class="flex items-center gap-2 px-4 py-2 bg-[#F36421] text-white rounded-xl text-sm font-medium hover:bg-[#d95214] shadow-lg shadow-[#F36421]/20 transition-all cursor-pointer">
          <Plus class="w-4 h-4" />
          Nueva Solicitud / Cotizar
        </button>
      </div>
    </div>

    <!-- TARJETA PRINCIPAL Y MÉTRICAS REALES DE LLEVO -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <div class="lg:col-span-2 bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 text-white relative overflow-hidden shadow-xl">
        <div class="absolute top-0 right-0 w-64 h-64 bg-[#F36421]/20 blur-3xl rounded-full -mr-16 -mt-16 pointer-events-none"></div>

        <div class="relative z-10 flex flex-col h-full justify-between gap-6">
          <div>
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-medium backdrop-blur-sm border border-emerald-500/20 mb-4">
              <Activity class="w-3 h-3" /> Motor OSRM & Base de Datos: Sincronizados
            </div>
            <h2 class="text-2xl font-bold max-w-md leading-snug">
              Panel de Control Logístico (LLEVO)
            </h2>
            <p class="text-slate-300 mt-2 max-w-sm text-sm">
              Control de fletes en calle, ingresos netos de agencia (25%) y flota activa de motorizados.
            </p>
          </div>

          <!-- MÉTRICAS REALES DE LLEVO -->
          <div class="flex flex-col sm:flex-row gap-4">

            <!-- Fletes Totales / Ingresos Hoy -->
            <div class="bg-white/5 backdrop-blur-md border border-white/10 p-4 rounded-2xl flex-1">
              <div class="flex items-center justify-between mb-2">
                <Banknote class="w-5 h-5 text-[#F36421]" />
                <span class="text-xs text-slate-400">Fletes</span>
              </div>
              <p v-if="!loading" class="text-2xl font-bold font-mono">C$ {{ formatMoney(metrics.totalRevenueToday) }}</p>
              <div v-else class="h-8 w-24 bg-white/10 animate-pulse rounded"></div>
              <p class="text-xs text-slate-400 mt-1">Facturado en Fletes Hoy</p>
            </div>

            <!-- Ganancia Limpia Agencia (25%) -->
            <div class="bg-white/5 backdrop-blur-md border border-white/10 p-4 rounded-2xl flex-1">
              <div class="flex items-center justify-between mb-2">
                <Percent class="w-5 h-5 text-emerald-400" />
                <span class="text-xs text-slate-400">Margen 25%</span>
              </div>
              <p v-if="!loading" class="text-2xl font-bold font-mono text-emerald-400">${{ formatMoney(metrics.agencyUsdTotal) }} USD</p>
              <div v-else class="h-8 w-16 bg-white/10 animate-pulse rounded"></div>
              <p class="text-xs text-slate-400 mt-1">Ganancia Neta LLEVO</p>
            </div>

            <!-- Pedidos Activos / En Curso -->
            <div class="bg-white/5 backdrop-blur-md border border-white/10 p-4 rounded-2xl flex-1">
              <div class="flex items-center justify-between mb-2">
                <Truck class="w-5 h-5 text-blue-400" />
                <span class="text-xs text-slate-400">En Calle</span>
              </div>
              <p v-if="!loading" class="text-2xl font-bold font-mono">{{ metrics.activeOrdersCount }}</p>
              <div v-else class="h-8 w-16 bg-white/10 animate-pulse rounded"></div>
              <p class="text-xs text-slate-400 mt-1">Órdenes Asignadas / En Ruta</p>
            </div>

          </div>
        </div>
      </div>

      <!-- ÚLTIMOS MOVIMIENTOS / ÓRDENES RECIENTES -->
      <div class="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col">
        <h3 class="font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
          <History class="w-4 h-4 text-[#F36421]" /> Últimos Despachos
        </h3>

        <!-- Skeleton Loader -->
        <div v-if="loading" class="space-y-4">
          <div v-for="n in 4" :key="n" class="h-10 bg-slate-100 dark:bg-slate-800 animate-pulse rounded-xl"></div>
        </div>

        <div v-else-if="recentOrders.length > 0" class="flex-1 space-y-4 overflow-y-auto pr-2 custom-scrollbar max-h-[300px]">
          <div v-for="order in recentOrders" :key="order.id" class="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800">
            <div>
              <p class="text-xs font-mono font-bold text-slate-900 dark:text-white">
                #{{ order.tracking_code || `LLEVO-${order.id}` }}
              </p>
              <p class="text-[11px] text-slate-500 truncate max-w-[180px]">
                {{ order.pickup_address }}
              </p>
            </div>
            <div class="text-right font-mono">
              <p class="text-xs font-bold text-[#F36421]">C$ {{ formatMoney(order.total_delivery_fee) }}</p>
              <span class="inline-block text-[9px] px-2 py-0.5 rounded font-bold" :class="getStatusBadgeClass(order.status)">
                {{ order.status }}
              </span>
            </div>
          </div>
        </div>

        <div v-else class="text-xs text-slate-400 text-center my-auto">
          No hay despachos recientes en el sistema.
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import apiClient from '@/api/client';
import {
  ArrowRight, Activity, Bell, Plus,
  Banknote, Percent, Truck, History, LayoutDashboard,
  Package, Users, Navigation, ShieldCheck
} from 'lucide-vue-next';

const router = useRouter();

const loading = ref(true);
const metrics = ref({
  totalRevenueToday: 0,
  agencyUsdTotal: 0,
  activeOrdersCount: 0
});
const recentOrders = ref([]);

// Fecha y Saludo
const today = new Date();
const formattedDate = today.toLocaleDateString('es-NI', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

const greeting = computed(() => {
  const hour = today.getHours();
  if (hour < 12) return 'Buenos días';
  if (hour < 18) return 'Buenas tardes';
  return 'Buenas noches';
});

// Módulos específicos del software LLEVO
// Consumo de datos reales desde el Backend de LLEVO
const fetchLlevoDashboardData = async () => {
  loading.value = true;
  try {
    const resOrders = await apiClient.get('/orders');
    const orders = Array.isArray(resOrders.data) ? resOrders.data : (resOrders.data.data || []);

    // Calcular métricas reales en base a las órdenes de la BD
    let revenueSum = 0;
    let agencyUsdSum = 0;
    let activeCount = 0;

    orders.forEach(o => {
      const fee = Number(o.total_delivery_fee || 0);
      const usd = Number(o.agency_usd || (fee * 0.25) / 36.62);
      revenueSum += fee;
      agencyUsdSum += usd;

      if (o.status === 'ASSIGNED' || o.status === 'IN_TRANSIT') {
        activeCount++;
      }
    });

    metrics.value = {
      totalRevenueToday: revenueSum,
      agencyUsdTotal: agencyUsdSum,
      activeOrdersCount: activeCount
    };

    // Tomar las últimas 5 órdenes para el historial
    recentOrders.value = orders.slice(0, 5);

  } catch (error) {
    console.error('Error cargando métricas de LLEVO:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchLlevoDashboardData();
});

const formatMoney = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getStatusBadgeClass = (status) => {
  const map = {
    'PENDING': 'bg-amber-100 text-amber-800',
    'ASSIGNED': 'bg-blue-100 text-blue-800',
    'IN_TRANSIT': 'bg-purple-100 text-purple-800',
    'DELIVERED': 'bg-emerald-100 text-emerald-800',
    'CANCELLED': 'bg-red-100 text-red-800'
  };
  return map[status] || 'bg-slate-100 text-slate-800';
};

const openCreateOrder = () => {
  router.push('/orders'); // O navega a tu vista/modal de órdenes
};
</script>

<style scoped>
@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in-up 0.5s ease-out forwards;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 20px;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #334155;
}
</style>